import _ from '@sparta.global.vendors/lodash';
import footnoteTemplate from '@sparta.global.templates/footnote-suplink/dist/es6.js';
import glossaryLinkTemplate from '@sparta.global.templates/glossary-link/dist/es6.js';
import glossaryTermsTemplate from '@sparta.global.templates/glossary-terms/dist/es6.js';
import abbrTemplate from '@sparta.global.templates/abbr-template/dist/es6.js';
import linkTemplate from '@sparta.global.templates/link-template/dist/es6.js';

let eArtifact = {};
let getEmbeddedJcr = null;
let strings = {};
let WEB_ASSET_TOKEN = /(?:\/WEB_ASSET\/((?:(?:[a-z]{2}|\[language\])\.{1})?([^"')\\\/}]*)))/;
const regexps = {
  END_SLASH: /\/$/,
  START_SLASH: /^\//,
  IS_ABSOLUTE_URL: /^https?:\/\//,
  STRIP_BEGINNING_AND_END_SLASHES_G: /^\/+|\/+$/g,
  IS_VALID_LINK_ID: /^[A-Za-z]+[\w-:.]*$/,
  IS_VALID_ABBR_ID: /^[A-Za-z]+[\w-:.]*$/,
  IMAGE_TOKEN: /\\?\{IMAGE\|\|(.+?)(?:\|\| *((?:no-)?(fingerprint|cdn|cdn-no-fingerprint)|global) *)?\}/g,
  GLOSSARY_TOKEN: /\{GLOSSARY\|\|((?:(?:\{(?:(?:\{(?:[^{}])*\})|(?:[^{}]))*\})|(?:[^{}]))*)(?:\|\| *(freeform) *)*\}/g,
  FOOTNOTE_TOKEN: /\{FOOTNOTE\|\|((?:(?:\{(?:(?:\{(?:[^{}])*\})|(?:[^{}]))*\})|(?:[^{}]))*)(?:\|\| *(freeform) *)*\}/g,
  LINK_TOKEN: /\\?\{LINK\|\|(.+?)\}/g,
  ABBR_TOKEN: /\{ABBR\|\|(.+?)\}/g,
  ADA_TOKEN: /<ADA>(.+?)<\\?\/ADA>/g,
  WEB_ASSET_TOKEN: new RegExp(WEB_ASSET_TOKEN, 'g'),
  UUIDREGEX: /^(?:([a-z]{2}|\[language\])\.{1})?([_a-zA-Z0-9-.]+)$/
};
const DEFAULTS = {
  assetPath: 'published-assets'
};
const isCDNDisabled = helperFlag => helperFlag === 'no-cdn' || helperFlag === 'no-cdn-no-fingerprint';
const getSpaAssetsUrl = (options, rootContext) => (options.context || '').replace(new RegExp(`${options.environment}\\/${rootContext}`, 'g'), '');
const fallbackToCDNOrSpaAssets = (options, endpoint, rootContext) => endpoint || getSpaAssetsUrl(options, rootContext);
const isFingerprintEnabled = helperFlag => helperFlag === 'fingerprint' || helperFlag === 'no-cdn';
function getEndpointWithCDN(options, helperFlag, endpoint, rootContext) {
  if (isCDNDisabled(helperFlag)) {
    return getSpaAssetsUrl(options, rootContext);
  }
  return fallbackToCDNOrSpaAssets(options, endpoint, rootContext);
}
function toUnixPath(p) {
  return _.isString(p) ? p.replace(/(\\+|\/\/+)/g, '/') : p;
}

/**
 * Processes images within a JCR string and replaces {{IMAGE||...}} with the build path.
 * If this image isn't found, and eArtifact error will be thrown.
 * This function pushes a list of images to be built into the addImageToBuild array.
 * @param {object<*>} data Various helper objects/methods from the buildJCR method.
 * @param {string} $0 From the replacement match.
 * @param {string} $1 From the replacement match.
 * @param {string} $2 From the replacement match.
 * @returns {string} The replaced string, containing the image build path.
 * @export
 */
async function processImages(data, $0, $1, $2 = 'fingerprint') {
  if ($0[0] === '\\') return $0.slice(1);
  const {
    options = {},
    jcr = {},
    manager = {},
    writeMgr = {},
    addImageToBuild = [],
    dependentJCRs = [],
    rootContext
  } = data;
  const helperFlag = $2.trim();
  const helperPath = $1.trim();
  let endpoint = _.isObject(options.endpoint) ? options.endpoint.full.replace(regexps.END_SLASH, '') : '';

  // "global" images are images that refer to a url context, rather than
  // an image in the artifacts folder. For these we just need to return the sanitized url/
  // If the path is relative, then we should prefix the cdn.
  if (helperFlag === 'global') {
    const isAbsolute = regexps.IS_ABSOLUTE_URL.test(helperPath);
    // User provided global absolute image path
    if (isAbsolute) return helperPath;
    // Endpoint would indicate that CDN is enabled
    return endpoint ? `${endpoint}/${helperPath.replace(regexps.START_SLASH, '')}` : helperPath;
  }
  if (typeof window === 'undefined') {
    if (helperPath.startsWith('/WEB_ASSET')) {
      return await processImageTokenWebAssets(data, $0, helperPath);
    }

    // User referenced image within the artifacts images/ directory
    if (_.startsWith(helperPath, 'assets')) {
      const image = await manager.getArtifacts({
        type: 'image',
        name: helperPath,
        first: true,
        throws: false
      });
      if (image) {
        dependentJCRs.push(image);
        endpoint = getEndpointWithCDN(options, helperFlag, endpoint, rootContext);
        const zone = writeMgr.writeZoneOf(image, options.context);
        const basepath = `${endpoint.replace(regexps.END_SLASH, '')}/${toUnixPath(zone.value).replace(options.context, '').replace(regexps.STRIP_BEGINNING_AND_END_SLASHES_G, '')}`;
        const fingerprintImage = isFingerprintEnabled(helperFlag);
        const opts = {
          ...options,
          endpoint,
          fingerprintImage
        };
        addImageToBuild({
          options: opts,
          artifact: image
        });
        const unixPath = `${basepath}/${toUnixPath(await writeMgr.getWriteFilenameOfArtifact(opts, image))}`;
        return unixPath;
      }
    }
    return eArtifact(strings.badJCRReference('image', jcr, helperPath));
  } else {
    if (helperPath.startsWith('/WEB_ASSET')) {
      return processImageTokenWebAssets(data, $0, helperPath);
    } else {
      endpoint = getEndpointWithCDN(options, helperFlag, endpoint, rootContext);
      const zone = `${options.context}\\spa-assets\\images`;
      const basepath = `${endpoint.replace(regexps.END_SLASH, '')}/${toUnixPath(zone).replace(options.context, '').replace(regexps.STRIP_BEGINNING_AND_END_SLASHES_G, '')}`;
      const unixPath = `${basepath}/${helperPath.replace(/\//g, '-')}`;
      return unixPath;
    }
  }
}

/**
 * Processes glossaries within a JCR string and replaces {{GLOSSARY||...}} with the glossary HTML.
 * This method pushes a list of glossaries to the gterms array using the addGlossaryTerm method.
 * @param {object<*>} data Various helper objects/methods from the buildJCR method.
 * @param {string} $0 From the replacement match.
 * @param {string} $1 From the replacement match.
 * @returns {string} The replaced string, containing the glossary build path.
 * @export
 */
async function processGlossary(data, $0, $1) {
  if ($0[0] === '\\') return $0.slice(1);
  const {
    jcr,
    addGlossaryTerm,
    config = {},
    orders,
    dependentJCRs,
    spartaContentManagerUtility
  } = data;
  if (typeof window === 'undefined') {
    if (data.jcr.ext !== 'json' || $1.startsWith('/WEB_CONTENT')) return $0;
    const embeddedJCR = await getEmbeddedJcr($0, $1, data);
    if (embeddedJCR) {
      dependentJCRs.push(embeddedJCR);
      const {
        name,
        label,
        content
      } = JSON.parse(await embeddedJCR.load());
      addGlossaryTerm(glossaryTermsTemplate.render({
        glossaryTerms: [{
          name,
          label,
          content
        }]
      }));
      return glossaryLinkTemplate.render({
        name,
        index: ++orders.glossary,
        label
      });
    }
    return eArtifact(strings.badJCRReference('glossary', jcr, $1));
  } else {
    const {
      name,
      label,
      content
    } = config[$1] || (await spartaContentManagerUtility.getContent({
      key: $1
    }));
    const spartaContainer = document.querySelector('[data-sparta-container]');
    spartaContainer.insertAdjacentHTML('beforeend', glossaryTermsTemplate.render({
      glossaryTerms: [{
        name,
        label,
        content
      }]
    }));
    return glossaryLinkTemplate.render({
      name,
      index: ++orders.glossary,
      label
    });
  }
}

/**
 * Processes footnotes within a JCR string and replaces {{FOOTNOTE||...}} with footnote HTML.
 * This method pushes a list of footnotes to the footnotes array using the addFootnote method.
 * @param {object<*>} data Various helper objects/methods from the buildJCR method.
 * @param {string} $0 From the replacement match.
 * @param {string} $1 From the replacement match.
 * @returns {string} The replaced string, containing the footnote HTML.
 * @export
 */
async function processFootnotes(data, $0, $1) {
  if ($0[0] === '\\') return $0.slice(1);
  const {
    addFootnote,
    config = {},
    orders,
    dependentJCRs,
    jcr,
    path,
    spartaContentManagerUtility
  } = data;
  if (typeof window === 'undefined') {
    if (data.jcr.ext !== 'json' || $1.startsWith('/WEB_CONTENT')) return $0;
    const [dsn] = _.map(_.split($1, '||'), _.trim);
    const basepath = path.parse(dsn || '');
    // To check if datasource is shield key
    if (basepath.dir.indexOf('/') < 0 && basepath.base.indexOf('/') < 0 && basepath.base.indexOf('.') > -1) {
      const [contentKey, contentRecordKey] = $1.split('|');
      const footnote = {
        name: $1.replace('|', '.'),
        contentKey,
        contentRecordKey,
        adaText: 'adatext',
        order: ++orders.footnote
      };
      addFootnote(footnote);
      return footnoteTemplate.render(footnote);
    }
    const embeddedJCR = await getEmbeddedJcr($0, $1, data);
    if (embeddedJCR) {
      dependentJCRs.push(embeddedJCR);
      const {
        name,
        label,
        content,
        adaText
      } = JSON.parse(await embeddedJCR.load());
      const footnote = {
        name,
        label,
        content,
        adaText: adaText || 'adatext',
        order: ++orders.footnote
      };
      addFootnote(footnote);
      return footnoteTemplate.render(footnote);
    }
    return eArtifact(strings.badJCRReference('footnote', jcr, $1));
  } else {
    const footnoteOrder = ++orders.footnote;
    const {
      name = `${footnoteOrder}_fn`,
      label = `"${footnoteOrder}_fn_label"`,
      content = "",
      adaText
    } = config[$1] || (await spartaContentManagerUtility.getContent({
      key: $1
    }));
    const footnote = {
      name,
      label,
      content,
      adaText: adaText || 'adatext',
      order: footnoteOrder
    };
    return footnoteTemplate.render(footnote);
  }
}

/**
 * Processes links within a JCR string and replaces {{LINK||...}} with link HTML.
 * This method pushes a list of footnotes to the footnotes array using the addFootnote method.
 * @param {object<*>} data Various helper objects/methods from the buildJCR method.
 * @param {string} $0 From the replacement match.
 * @param {string} $1 From the replacement match.
 * @returns {string} The replaced string, containing the link HTML.
 * @export
 */
async function processLinks(data, $0, $1) {
  if ($0[0] === '\\') return $0.slice(1);
  const {
    jcr,
    config = {},
    spartaContentManagerUtility
  } = data;
  if (typeof window === 'undefined') {
    if (data.jcr.ext !== 'json' || $1.startsWith('/WEB_CONTENT')) return $0;
    const links = config.LINKS || config.links;
    if (!_.isObject(links)) return eArtifact(strings.cannotPerformLinkSubstitution(jcr));
    const linkID = $1;

    // Fail if links object or linkID does not exist.
    if (!links[linkID]) return eArtifact(strings.badLinkId(jcr, linkID));
    const link = {
      ...links[linkID]
    };

    // Testing ID formatting
    if (!regexps.IS_VALID_LINK_ID.test(linkID)) {
      return eArtifact(strings.invalidLinkFormat(jcr, linkID));
    }
    // Replace link tag with expanded anchor <a> tag
    return linkTemplate.render({
      link
    });
  } else {
    let link = {};
    const linkID = $1;
    if (linkID.includes('WEB_CONTENT/')) {
      link = await spartaContentManagerUtility.getContent({
        key: linkID
      });
    } else {
      const links = config.LINKS || config.links || config;
      link = {
        ...links[linkID]
      };
    }
    return linkTemplate.render({
      link
    });
  }
}

/**
 * Processes ABBR text within a JCR string and replaces {{ABBR||...}} with link HTML.
 * @param {object<*>} data Various helper objects/methods from the buildJCR method.
 * @param {string} $0 From the replacement match.
 * @param {string} $1 From the replacement match.
 * @returns {string} The replaced string, containing the ADA HTML.
 * @export
 */
async function processABBR(data, $0, $1) {
  if (data?.jcr?.ext === 'md') return $0;
  const {
    jcr,
    config = {},
    spartaContentManagerUtility
  } = data;
  if (typeof window === 'undefined') {
    if ($1.startsWith('/WEB_CONTENT')) return $0;
    const abbrs = config.ABBRS || config.abbrs;
    if (!_.isObject(abbrs)) return eArtifact(strings.cannotPerformAbbrSubstitution(jcr));
    const abbrID = $1;

    // Fail if abbrs object or abbrID does not exist.
    if (!abbrs[abbrID]) return eArtifact(strings.badAbbrId(jcr, abbrID));
    const abbr = {
      ...abbrs[abbrID]
    };

    // Testing ID formatting
    if (!regexps.IS_VALID_ABBR_ID.test(abbrID)) {
      return eArtifact(strings.invalidAbbrFormat(jcr, abbrID));
    }

    // Replace link tag with expanded anchor <a> tag
    return abbrTemplate.render({
      abbr
    });
  } else {
    let abbr = {};
    const abbrID = $1;
    if (abbrID.includes('WEB_CONTENT/')) {
      abbr = await spartaContentManagerUtility.getContent({
        key: abbrID
      });
    } else {
      const abbrs = config.ABBRS || config.abbrs || config;
      abbr = {
        ...abbrs[abbrID]
      };
      // delete meta to avoid it being added as an <abbr> attribute
      if (abbr.__meta__) {
        delete abbr.__meta__;
      }
    }
    return abbrTemplate.render({
      abbr
    });
  }
}

/**
 * Processes ADA text within a JCR string and replaces <ADA>.*</ADA> with the ADA HTML.
 * @param {object<*>} data Various helper objects/methods from the buildJCR method.
 * @param {string} $0 From the replacement match.
 * @param {string} $1 From the replacement match.
 * @returns {string} The replaced string, containing the ADA HTML.
 * @export
 */
async function processADA(data, $0, $1) {
  return `<span class="ada-hidden">${$1}</span>`;
}

/**
 * Processes assets within a JCR/Web content's Image helper token.
 * @param {object<*>} data Various helper objects/methods from the buildJCR method.
 * @param {string} ImageToken Orginal Image Token.
 * @param {string} webAssetToken Web Asset Token with UUID.
 * @returns {string} The replaced string, containing the image build path.
 * @export
 */
async function processImageTokenWebAssets(data, imageToken, webAssetToken) {
  const webAssetKey = webAssetToken.match(WEB_ASSET_TOKEN);
  const resolvedWebAssetPath = await processWebAssets(data, webAssetKey[0], webAssetKey[1]);
  if (resolvedWebAssetPath.startsWith('/WEB_ASSET')) return imageToken;
  return resolvedWebAssetPath;
}

/**
 * Processes webContent assets within a JCR string and replaces WEB_ASSET with the webContentCms host.
 * @param {object<*>} data Various helper objects/methods from the buildJCR method.
 * @param {string} $0 From the replacement match.
 * @param {string} $1 From the replacement match.
 * @returns {string} The replaced string, containing the image build path.
 * @export
 */
async function processWebAssets(data, $0, $1) {
  if (data?.jcr?.ext === 'md') return $0;
  const keyFragments = $1 && $1.match(regexps.UUIDREGEX);
  if (!keyFragments) {
    if (data.log) {
      data?.log?.error(`Invalid WEB_ASSET UUID ${$1} found in ${data.config?.jcr}`);
    } else {
      console.error(`Invalid WEB_ASSET UUID ${$1}`);
    }
    return $0.replace(/\/WEB_ASSET\/\[language\]\./g, '/WEB_ASSET' + '/{language}.');
  }
  const {
    envVariables,
    language
  } = data;
  const webAssetKey = $1.replace('[language]', language);
  const endPointUrl = createWebAssetURL(envVariables);
  return `${endPointUrl}/${webAssetKey}`;
}
function createWebAssetURL(envVariables) {
  const {
    cdn,
    cms,
    domain
  } = envVariables;
  const host = cdn?.enabled ? cdn.host : cms?.assetHost || domain.hostname;
  const assetPath = cms?.assetPath || DEFAULTS.assetPath;
  return `${host}/${assetPath}`;
}
async function asyncStringReplace(haystack, needle, replacer) {
  const promises = [];
  const safe = _.isRegExp(needle) ? needle.source : _.escapeRegExp(needle);
  const ignoreCase = _.isRegExp(needle) ? needle.ignoreCase : false;
  const re = new RegExp(safe, `g${ignoreCase ? 'i' : ''}`);
  const str = haystack;
  let replaced = haystack;
  const p = async matches => {
    const substitute = _.isFunction(replacer) ? await replacer(...matches) : replacer;
    if (_.isString(substitute)) replaced = replaced.replace(matches[0], substitute);
  };
  let m;
  while (m = re.exec(str)) promises.push(p(m)); // eslint-disable-line no-cond-assign
  await Promise.all(promises);
  return replaced;
}
function escapeForJSON(str) {
  if (!_.isString(str)) return str;
  return str.replace(/[\\]/g, '\\\\').replace(/["]/g, '\\"').replace(/[/]/g, '\\/').replace(/[\b]/g, '\\b').replace(/[\f]/g, '\\f').replace(/[\n]/g, '\\n').replace(/[\r]/g, '\\r').replace(/[\t]/g, '\\t');
}
const tokenTasks = [{
  regexp: regexps.IMAGE_TOKEN,
  action: processImages
}, {
  regexp: regexps.GLOSSARY_TOKEN,
  action: processGlossary
}, {
  regexp: regexps.FOOTNOTE_TOKEN,
  action: processFootnotes
}, {
  regexp: regexps.LINK_TOKEN,
  action: processLinks
}, {
  regexp: regexps.ABBR_TOKEN,
  action: processABBR
}, {
  regexp: regexps.ADA_TOKEN,
  action: processADA
}, {
  regexp: regexps.WEB_ASSET_TOKEN,
  action: processWebAssets
}];
async function mapSeries(array, asyncCallback) {
  const results = [];
  for (const item of array) {
    const result = await asyncCallback(item);
    results.push(result);
  }
  return results;
}
class SpartaContentTokensUtility {
  async resolveTokens(data = {}) {
    if (typeof data != 'object' || !data?.contents) return;
    const {
      tasks = [],
      contents,
      escapeResult = true
    } = data;
    eArtifact = data.eArtifact;
    getEmbeddedJcr = data.getEmbeddedJcr;
    strings = data.strings;
    let replaced = contents.replace(/\/WEB_ASSET\/\{language\}\./g, '/WEB_ASSET' + '/[language].');

    // Returns a task function that's called by asyncStringReplace
    const curryTask = task => (...args) => task.action(data, ...args).then(escapeResult ? escapeForJSON : _.identity);

    // We must perform the string replacement tasks serially to prevent race conditions.
    await (Promise.mapSeries || mapSeries)([...tokenTasks, ...tasks], async task => {
      replaced = await asyncStringReplace(replaced, task.regexp, curryTask(task));
    });
    return replaced;
  }
  createWebAssetURL(envVariables) {
    return createWebAssetURL(envVariables);
  }
}
var index = new SpartaContentTokensUtility();

export { index as default };
